import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { upgradeEvents } from '../context/AuthContext';
import UpgradeModal from './UpgradeModal';
import api from '../utils/api';
import { 
  LayoutDashboard, 
  FileText, 
  PlusSquare, 
  Users, 
  Package, 
  BarChart3, 
  Settings, 
  LogOut,
  ChevronLeft,
  Menu,
  Sun,
  Moon,
  Plus,
  X } from 'lucide-react';

const Layout = ({ children }) => {
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();

  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    const saved = localStorage.getItem('sidebar-open');
    return saved !== null ? JSON.parse(saved) : true;
  });

  const [sidebarTheme, setSidebarTheme] = useState(() => {
    const savedTheme = localStorage.getItem('sidebar-theme');
    return savedTheme !== null ? savedTheme : 'dark';
  });

  const toggleSidebarTheme = () => {
    const newTheme = sidebarTheme === 'dark' ? 'light' : 'dark';
    setSidebarTheme(newTheme);
    localStorage.setItem('sidebar-theme', newTheme);
  };

  const [settings, setSettings] = useState(null);
  const [isFabOpen, setIsFabOpen] = useState(false);
  const [upgradeModal, setUpgradeModal] = useState({ open: false, feature: null, requiredPlan: 'professional', message: '' });

  // Listen for plan upgrade events emitted from the api interceptor
  useEffect(() => {
    const handler = (data) => {
      setUpgradeModal({ open: true, ...data });
    };
    upgradeEvents.on(handler);
    return () => upgradeEvents.off(handler);
  }, []);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await api.get('/settings');
        setSettings(response.data.settings);
      } catch (err) {
        console.error('Failed to load settings in Layout', err);
      }
    };
    fetchSettings();
  }, []);

  useEffect(() => {
    if (settings?.themeColor) {
      document.documentElement.style.setProperty('--accent', settings.themeColor);
    }
  }, [settings?.themeColor]);

  const handleToggleSidebar = (val) => {
    setIsSidebarOpen(val);
    localStorage.setItem('sidebar-open', JSON.stringify(val));
  };

  const isActive = (path) => {
    return location.pathname === path ? 'active' : '';
  };

  return (
    <div className="layout">
      <aside className={`sidebar ${!isSidebarOpen ? 'collapsed' : ''} ${sidebarTheme === 'light' ? 'light-mode' : ''}`}>
        <div className="sidebar-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {settings?.logo ? (
            <img src={settings.logo} alt="Company Logo" style={{ maxHeight: '40px', maxWidth: '140px', objectFit: 'contain' }} />
          ) : (
            <h2>Invoice System</h2>
          )}
          <button 
            onClick={() => handleToggleSidebar(false)}
            style={{
              background: 'transparent',
              border: 'none',
              color: sidebarTheme === 'light' ? 'rgba(0, 0, 0, 0.55)' : 'rgba(255, 255, 255, 0.55)',
              cursor: 'pointer',
              padding: '6px',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s',
              marginLeft: '10px'
            }}
            title="Collapse Sidebar"
            className="sidebar-toggle-btn"
          >
            <ChevronLeft size={20} />
          </button>
        </div>

        <div className="user-section" style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.08)', paddingBottom: '16px' }}>
          <div className="user-info">
            <div className="user-avatar">
              {user?.name ? user.name.charAt(0).toUpperCase() : 'U'}
            </div>
            <div>
              <div>{user?.name}</div>
              <div className="user-role">{user?.role?.toUpperCase()}</div>
            </div>
          </div>
        </div>

        <nav>
          <ul className="nav-menu">
            <li className="nav-item">
              <Link to="/dashboard" className={`nav-link ${isActive('/dashboard')}`}>
                <LayoutDashboard size={18} /> Dashboard
              </Link>
            </li>
            <li className="nav-item">
              <Link to="/invoice" className={`nav-link ${isActive('/invoice')}`}>
                <FileText size={18} /> Invoices
              </Link>
            </li>
            <li className="nav-item">
              <Link to="/invoice/create" className={`nav-link ${isActive('/invoice/create')}`}>
                <PlusSquare size={18} /> Create Invoice
              </Link>
            </li>
            <li className="nav-item">
              <Link to="/customers" className={`nav-link ${isActive('/customers')}`}>
                <Users size={18} /> Customers
              </Link>
            </li>
            <li className="nav-item">
              <Link to="/products" className={`nav-link ${isActive('/products')}`}>
                <Package size={18} /> Products
              </Link>
            </li>
            <li className="nav-item">
              <Link to="/reports" className={`nav-link ${isActive('/reports')}`}>
                <BarChart3 size={18} /> Reports
              </Link>
            </li>
            <li className="nav-item">
              <Link to="/settings" className={`nav-link ${isActive('/settings')}`}>
                <Settings size={18} /> Settings
              </Link>
            </li>
          </ul>
        </nav>

        <div className="sidebar-footer">
          <button 
            onClick={toggleSidebarTheme} 
            className="theme-toggle-btn"
          >
            {sidebarTheme === 'dark' ? (
              <><Sun size={18} /> Light Mode</>
            ) : (
              <><Moon size={18} /> Dark Mode</>
            )}
          </button>
          
          <button onClick={logout} className="logout-btn">
            <LogOut size={18} /> Logout
          </button>
        </div>
      </aside>

      <UpgradeModal
        isOpen={upgradeModal.open}
        onClose={() => setUpgradeModal(m => ({ ...m, open: false }))}
        feature={upgradeModal.feature}
        requiredPlan={upgradeModal.requiredPlan}
        message={upgradeModal.message}
      />

      {!isSidebarOpen && (
        <button 
          onClick={() => handleToggleSidebar(true)} 
          style={{
            position: 'fixed',
            top: '24px',
            left: '20px',
            zIndex: 1020,
            background: 'var(--accent)',
            color: '#ffffff',
            border: 'none',
            borderRadius: '8px',
            width: '38px',
            height: '38px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: 'var(--shadow-md)',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            outline: 'none'
          }}
          title="Expand Sidebar"
          onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
        >
          <Menu size={20} />
        </button>
      )}

      <main className={`main-content ${!isSidebarOpen ? 'expanded' : ''}`}>
        {children}
      </main>

    </div>
  );
};

export default Layout;
